import { Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/contexts/LocaleContext';
import {
  LOCALE_LABELS,
  LOCALE_SHORT_LABELS,
  type AppLocale,
} from '@/i18n/locales';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface LanguageSwitcherProps {
  /**
   * - `segmented` (default): three inline pills (EN | 日本 | عربي), used in
   *   expanded sidebar, mobile drawer, and the landing page nav.
   * - `dropdown`: single globe icon → menu of full names. Used in collapsed
   *   sidebar where horizontal space is too tight for three pills.
   */
  variant?: 'segmented' | 'dropdown';
  /** Optional className passed to the wrapper. */
  className?: string;
  /**
   * Compatibility shim — old call sites used `compact={true}` to mean
   * "collapsed sidebar". Maps to `variant="dropdown"`.
   */
  compact?: boolean;
}

export function LanguageSwitcher({
  variant,
  className,
  compact = false,
}: LanguageSwitcherProps) {
  const { t } = useTranslation('common');
  const { locale, setLocale, supportedLocales } = useLocale();

  const resolvedVariant: 'segmented' | 'dropdown' =
    variant ?? (compact ? 'dropdown' : 'segmented');

  if (resolvedVariant === 'dropdown') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t('language')}
            title={t('language')}
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-lg p-0 font-bold text-brand-indigo hover:bg-brand-teal-soft',
              className,
            )}
          >
            <Languages className="h-4 w-4" strokeWidth={2.25} />
            <span className="sr-only">{LOCALE_LABELS[locale]}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="right"
          align="start"
          className="border-2 border-brand-indigo bg-k-card"
        >
          {supportedLocales.map((code) => (
            <DropdownMenuItem
              key={code}
              onClick={() => setLocale(code)}
              className={cn(
                'cursor-pointer font-semibold text-brand-indigo focus:bg-brand-teal-soft',
                code === locale && 'bg-brand-teal text-brand-indigo',
              )}
            >
              <span className="font-mono text-[10px] mr-2 inline-block w-7 text-brand-indigo/70">
                {LOCALE_SHORT_LABELS[code]}
              </span>
              {LOCALE_LABELS[code]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Segmented control — three explicit pills
  return (
    <div
      role="group"
      aria-label={t('language')}
      className={cn(
        'inline-flex items-stretch gap-0 rounded-lg border-2 border-brand-indigo bg-brand-cream p-0.5',
        className,
      )}
    >
      {supportedLocales.map((code) => {
        const active = code === locale;
        return (
          <button
            key={code}
            type="button"
            aria-pressed={active}
            aria-label={LOCALE_LABELS[code]}
            onClick={() => setLocale(code)}
            className={cn(
              'inline-flex min-w-[44px] items-center justify-center rounded-md px-2.5 py-1 text-xs font-bold transition-colors',
              active
                ? 'bg-brand-indigo text-brand-cream'
                : 'text-brand-indigo hover:bg-brand-teal-soft',
            )}
          >
            {LOCALE_SHORT_LABELS[code as AppLocale]}
          </button>
        );
      })}
    </div>
  );
}
